import { useState, useEffect, useCallback, useRef } from 'react';
import { 
    isFullscreenSupported, 
    isFullscreen, 
    enterFullscreen, 
    exitFullscreen, 
    toggleFullscreen, 
    onFullscreenChange,
    requestFullscreenOnInteraction 
} from '../utils/fullscreen';

/**
 * React hook for managing fullscreen state
 */
export function useFullscreen(element?: HTMLElement) {
    const [isFullscreenState, setIsFullscreenState] = useState(false);
    const [isSupported] = useState(isFullscreenSupported());
    const elementRef = useRef<HTMLElement | null>(element || null);

    // Update element ref if passed element changes
    useEffect(() => {
        elementRef.current = element || null;
    }, [element]);

    // Listen for fullscreen changes
    useEffect(() => {
        const cleanup = onFullscreenChange((fullscreenState) => {
            setIsFullscreenState(fullscreenState);
        });

        // Set initial state
        setIsFullscreenState(isFullscreen());

        return cleanup;
    }, []);

    // Enter fullscreen
    const enter = useCallback(async () => {
        if (!isSupported) return false;
        return await enterFullscreen(elementRef.current || undefined);
    }, [isSupported]);

    // Exit fullscreen
    const exit = useCallback(async () => {
        if (!isSupported) return false;
        return await exitFullscreen();
    }, [isSupported]);

    // Toggle fullscreen
    const toggle = useCallback(async () => {
        if (!isSupported) return false;
        return await toggleFullscreen(elementRef.current || undefined);
    }, [isSupported]);

    // Request fullscreen on user interaction
    const requestOnInteraction = useCallback(() => {
        if (!isSupported) return Promise.resolve(false);
        return requestFullscreenOnInteraction(elementRef.current || undefined);
    }, [isSupported]);

    return {
        isFullscreen: isFullscreenState,
        isSupported,
        enter,
        exit,
        toggle,
        requestOnInteraction
    };
}

/**
 * Hook for automatically requesting fullscreen on app launch
 * This will wait for the first user interaction to trigger fullscreen
 */
export function useFullscreenOnLaunch(element?: HTMLElement, enabled: boolean = true) {
    const [hasTriggered, setHasTriggered] = useState(false);
    const { requestOnInteraction, isSupported } = useFullscreen(element);

    useEffect(() => {
        if (!enabled || !isSupported || hasTriggered) return;

        // Request fullscreen on first user interaction
        requestOnInteraction().then((success) => {
            if (success) {
                setHasTriggered(true);
            }
        });
    }, [enabled, isSupported, hasTriggered, requestOnInteraction]);

    return {
        hasTriggered,
        isSupported
    };
} 